// Meal-plan entry detail — per-day customization sheet that opens
// when the user taps a recipe-linked meal-plan cell. Lets the user
// swap, remove, or add ingredients FOR THAT ONE DAY only; the
// canonical recipe is untouched, no other days are affected.
//
// LLM-assisted picker: when the user taps "Swap" on an ingredient, an
// inline picker offers "Ask for suggestions" which calls the chat LLM
// (via mealplan.suggestSubstitutes) for 3-5 ranked alternatives.
//
// Bottom-sheet on mobile, side-panel on desktop — matches RecipeDetail.

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { api, type EffectiveMealItem, type MealSlot, type SubstitutionSuggestion } from "../../lib/rpc";
import type { NotifyFn } from "../../components/toast";

interface MealPlanEntryDetailProps {
  date: string;
  slot: MealSlot;
  /** Tapping "Open the canonical recipe" routes here. Same hook the
   *  cell-tap used to call directly; we surface it now under "View
   *  recipe" so the per-day customization sheet is the new default. */
  onOpenRecipe?: (recipeId: string) => void;
  /** Cook the meal-plan entry with its per-day overlay applied. The
   *  parent opens Cook Mode with entryRef={{date, slot}} so cooking
   *  reflects swaps / additions / removals the user made for this
   *  specific day, not the canonical recipe. */
  onCook?: (recipeId: string) => void;
  onClose: () => void;
  notify: NotifyFn;
}

export function MealPlanEntryDetail({
  date, slot, onOpenRecipe, onCook, onClose, notify,
}: MealPlanEntryDetailProps) {
  const [loading, setLoading] = useState(true);
  const [recipeName, setRecipeName] = useState<string>("");
  const [recipeId, setRecipeId] = useState<string | null>(null);
  const [items, setItems] = useState<EffectiveMealItem[]>([]);
  const [swapping, setSwapping] = useState<EffectiveMealItem | null>(null);
  const [adding, setAdding] = useState(false);
  const panelRef = useRef<HTMLElement>(null);

  // Swipe-down-to-close gesture — matches the RecipeDetail bottom
  // sheet's affordance for consistency. Guards on scrollTop === 0 so
  // long ingredient lists keep their native scroll. See
  // recipe-detail.tsx for the full rationale on the threshold +
  // velocity calc; this is the same shape, kept inline rather than
  // factored because the touch handler's a small chunk of code that
  // reads more clearly when it lives next to the panel it controls.
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    let startY = 0;
    let startedAtTop = false;
    let committed = false;
    let lastY = 0;
    let lastT = 0;
    let velocity = 0;
    const COMMIT_PX = 8, CLOSE_PX = 120, FLICK_PX = 60, FLICK_VEL = 0.5;
    const onTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      startY = t.clientY;
      lastY = startY;
      lastT = performance.now();
      startedAtTop = el.scrollTop <= 0;
      committed = false;
      velocity = 0;
      el.style.transition = "";
    };
    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t || !startedAtTop) return;
      const dy = t.clientY - startY;
      if (dy <= 0) return;
      if (!committed) {
        if (dy < COMMIT_PX) return;
        committed = true;
      }
      const now = performance.now();
      const dt = Math.max(1, now - lastT);
      velocity = (t.clientY - lastY) / dt;
      lastY = t.clientY;
      lastT = now;
      el.style.transform = `translateY(${dy}px)`;
      const bg = el.parentElement;
      if (bg) bg.style.opacity = String(Math.max(0, 1 - dy / 400));
      e.preventDefault();
    };
    const onTouchEnd = () => {
      if (!committed) return;
      committed = false;
      const dy = parseFloat(el.style.transform.replace(/[^\d.-]/g, "")) || 0;
      const shouldClose = dy > CLOSE_PX || (dy > FLICK_PX && velocity > FLICK_VEL);
      el.style.transition = "transform 200ms cubic-bezier(.4,0,.2,1)";
      const bg = el.parentElement;
      if (bg) bg.style.transition = "opacity 200ms ease-out";
      if (shouldClose) {
        el.style.transform = "translateY(100vh)";
        if (bg) bg.style.opacity = "0";
        window.setTimeout(() => onClose(), 195);
      } else {
        el.style.transform = "";
        if (bg) bg.style.opacity = "";
      }
    };
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove",  onTouchMove,  { passive: false });
    el.addEventListener("touchend",   onTouchEnd,   { passive: true });
    el.addEventListener("touchcancel", onTouchEnd,  { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove",  onTouchMove);
      el.removeEventListener("touchend",   onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [onClose]);

  const refresh = async () => {
    try {
      const r = await api.mealplanGetEffectiveItems({ date, slot });
      setRecipeName(r.entry.value || "");
      setRecipeId(r.entry.recipeId);
      setItems(r.items);
    } catch (e: any) {
      notify(e?.message || "couldn't load meal plan entry");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [date, slot]);

  // Esc closes. Same pattern as the global palette + bell dropdown.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const isModified = useMemo(() => items.some((it) => it.isModified), [items]);

  const handleSwap = async (item: EffectiveMealItem, substituteText: string, substituteQty?: string | null) => {
    if (!substituteText.trim()) return;
    try {
      await api.mealplanSubstitute({
        date, slot,
        op: {
          op: "swap",
          recipeItemId: item.id,
          originalText: item.originalText || item.text,
          substituteText: substituteText.trim(),
          substituteQty: substituteQty || null,
        },
      });
      setSwapping(null);
      await refresh();
      notify(`Swapped ${item.originalText || item.text} → ${substituteText}`);
    } catch (e: any) { notify(e?.message || "swap failed"); }
  };

  const handleRemove = async (item: EffectiveMealItem) => {
    try {
      await api.mealplanSubstitute({
        date, slot,
        op: { op: "remove", recipeItemId: item.id, originalText: item.text },
      });
      await refresh();
      notify(`Removed ${item.text} for ${slot}`);
    } catch (e: any) { notify(e?.message || "remove failed"); }
  };

  const handleReset = async (item: EffectiveMealItem) => {
    try {
      const opName = item.modificationKind === "addition" ? "deleteAddition" : "unswap";
      const op = opName === "deleteAddition"
        ? { op: "deleteAddition" as const, id: String(item.id) }
        : { op: "unswap" as const, recipeItemId: item.id };
      await api.mealplanSubstitute({ date, slot, op });
      await refresh();
    } catch (e: any) { notify(e?.message || "reset failed"); }
  };

  const handleUnremove = async (recipeItemId: number) => {
    try {
      await api.mealplanSubstitute({
        date, slot,
        op: { op: "unremove", recipeItemId },
      });
      await refresh();
    } catch (e: any) { notify(e?.message || "couldn't restore"); }
  };

  const handleResetAll = async () => {
    try {
      await api.mealplanSubstitute({ date, slot, op: { op: "clear" } });
      await refresh();
      notify("Restored canonical recipe");
    } catch (e: any) { notify(e?.message || "reset failed"); }
  };

  const handleAdd = async (text: string, quantity?: string) => {
    if (!text.trim()) return;
    try {
      await api.mealplanSubstitute({
        date, slot,
        op: { op: "add", text: text.trim(), quantity: quantity || null },
      });
      setAdding(false);
      await refresh();
      notify(`Added ${text}`);
    } catch (e: any) { notify(e?.message || "add failed"); }
  };

  return (
    <div className="mpd-bg" onClick={onClose}>
      <aside
        ref={panelRef}
        className="mpd-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Customize meal"
      >
        <div className="mpd-drag-handle" aria-hidden />

        <header className="mpd-head">
          <div className="mpd-head-meta">
            <div className="mpd-eyebrow">{slot} · {fmtDate(date)}</div>
            <h2 className="mpd-title">
              {recipeName || "Loading…"}
              {isModified && <span className="mpd-modified-badge">Modified</span>}
            </h2>
          </div>
          <button className="icon-btn" onClick={onClose} title="Close" type="button">
            <Icon name="x" size={14} />
          </button>
        </header>

        <div className="mpd-actions-bar">
          {recipeId && onCook && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => onCook(recipeId)}
              title={isModified
                ? "Cook with your day-specific changes applied"
                : "Open the recipe in Cook Mode"}
            >
              <Icon name="flame" size={13} />
              <span>Cook this</span>
            </button>
          )}
          {recipeId && onOpenRecipe && (
            <button
              type="button"
              className="btn"
              onClick={() => onOpenRecipe(recipeId)}
              title="Open the canonical recipe (no overlay)"
            >
              <Icon name="arrow-right" size={13} />
              <span>View recipe</span>
            </button>
          )}
          {isModified && (
            <button
              type="button"
              className="btn"
              onClick={handleResetAll}
              title="Restore the canonical recipe for this day"
            >
              <Icon name="x" size={13} />
              <span>Reset all</span>
            </button>
          )}
        </div>

        <section className="mpd-section">
          <div className="mpd-section-label">Ingredients (this day only)</div>
          {loading && <p className="mpd-empty">loading…</p>}
          {!loading && items.length === 0 && (
            <p className="mpd-empty">No ingredients yet.</p>
          )}
          {!loading && items.map((it) => (
            <IngredientRow
              key={`${it.id}-${it.modificationKind || "ok"}`}
              item={it}
              swappingThis={swapping?.id === it.id && swapping?.modificationKind === it.modificationKind}
              recipeId={recipeId}
              onSwapStart={() => setSwapping(it)}
              onSwapCancel={() => setSwapping(null)}
              onSwapCommit={(text, qty) => handleSwap(it, text, qty)}
              onRemove={() => handleRemove(it)}
              onReset={() => handleReset(it)}
              notify={notify}
            />
          ))}
          {/* Removed rows live in the overlay but don't render as
            * normal ingredients. Surface them at the bottom with a
            * one-tap restore so they're not invisible. */}
          {!loading && <RemovedRows date={date} slot={slot} onUnremove={handleUnremove} />}
        </section>

        <section className="mpd-section">
          {adding ? (
            <AdditionForm
              onCommit={handleAdd}
              onCancel={() => setAdding(false)}
            />
          ) : (
            <button
              type="button"
              className="mpd-add-btn"
              onClick={() => setAdding(true)}
            >
              <Icon name="plus" size={13} />
              <span>Add an ingredient (for this day)</span>
            </button>
          )}
        </section>
      </aside>
    </div>
  );
}

function fmtDate(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

/** One ingredient row + per-row swap picker when active. */
function IngredientRow({
  item, swappingThis, recipeId,
  onSwapStart, onSwapCancel, onSwapCommit,
  onRemove, onReset, notify,
}: {
  item: EffectiveMealItem;
  swappingThis: boolean;
  recipeId: string | null;
  onSwapStart: () => void;
  onSwapCancel: () => void;
  onSwapCommit: (text: string, qty?: string) => void;
  onRemove: () => void;
  onReset: () => void;
  notify: NotifyFn;
}) {
  if (swappingThis) {
    return (
      <SwapPicker
        item={item}
        recipeId={recipeId}
        onCancel={onSwapCancel}
        onCommit={onSwapCommit}
        notify={notify}
      />
    );
  }
  const isSwap = item.modificationKind === "swap";
  const isAddition = item.modificationKind === "addition";
  return (
    <div className="mpd-ing-row" data-modified={item.isModified || undefined}>
      {item.emoji && <span className="mpd-ing-emoji">{item.emoji}</span>}
      <div className="mpd-ing-body">
        <div className="mpd-ing-text">{item.text}</div>
        {item.quantity && <div className="mpd-ing-qty">{item.quantity}</div>}
        {isSwap && item.originalText && (
          <div className="mpd-ing-orig">was: {item.originalText}</div>
        )}
        {isAddition && (
          <div className="mpd-ing-orig">added for this day</div>
        )}
      </div>
      <div className="mpd-ing-actions">
        {!isAddition && (
          <button type="button" className="mpd-row-btn" onClick={onSwapStart} title="Swap for something else">
            <Icon name="edit" size={11} />
          </button>
        )}
        {item.isModified ? (
          <button type="button" className="mpd-row-btn" onClick={onReset} title={isAddition ? "Remove this addition" : "Revert to original"}>
            <Icon name="x" size={11} />
          </button>
        ) : (
          <button type="button" className="mpd-row-btn" onClick={onRemove} title="Remove for this day">
            <Icon name="trash" size={11} />
          </button>
        )}
      </div>
    </div>
  );
}

/** Inline swap picker — shown in place of an ingredient row when the
 *  user taps "swap" on that row. */
function SwapPicker({
  item, recipeId, onCancel, onCommit, notify,
}: {
  item: EffectiveMealItem;
  recipeId: string | null;
  onCancel: () => void;
  onCommit: (text: string, qty?: string) => void;
  notify: NotifyFn;
}) {
  const [draftText, setDraftText] = useState("");
  const [draftQty, setDraftQty] = useState(item.quantity || "");
  const [userHint, setUserHint] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState<SubstitutionSuggestion[]>([]);

  const askLlm = async () => {
    if (!recipeId) {
      notify("recipe link missing — type a substitute below");
      return;
    }
    setSuggesting(true);
    setSuggestions([]);
    try {
      const r = await api.mealplanSuggestSubstitutes({
        recipeId,
        recipeItemId: item.id,
        userHint: userHint.trim() || undefined,
      });
      setSuggestions(r);
      if (r.length === 0) notify("No suggestions came back — type what you've got.");
    } catch (e: any) {
      notify(e?.message || "LLM unreachable — type a substitute manually");
    } finally {
      setSuggesting(false);
    }
  };

  const pick = (s: SubstitutionSuggestion) => {
    onCommit(s.text, s.quantity || undefined);
  };

  return (
    <div className="mpd-swap">
      <div className="mpd-swap-head">
        <div className="mpd-swap-original">
          <div className="mpd-swap-original-label">Swapping</div>
          <div className="mpd-swap-original-text">{item.originalText || item.text}{item.quantity ? ` (${item.quantity})` : ""}</div>
        </div>
        <button type="button" className="icon-btn" onClick={onCancel} title="Cancel">
          <Icon name="x" size={12} />
        </button>
      </div>
      <div className="mpd-swap-input">
        <input
          placeholder="What do you want to use instead?"
          autoFocus
          value={draftText}
          onInput={(e) => setDraftText((e.currentTarget as HTMLInputElement).value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && draftText.trim()) onCommit(draftText.trim(), draftQty || undefined);
          }}
        />
        <input
          placeholder="qty"
          value={draftQty}
          onInput={(e) => setDraftQty((e.currentTarget as HTMLInputElement).value)}
          className="mpd-swap-qty"
        />
        <button
          type="button"
          className="btn btn-primary"
          disabled={!draftText.trim()}
          onClick={() => onCommit(draftText.trim(), draftQty || undefined)}
        >
          Save
        </button>
      </div>

      <div className="mpd-swap-llm">
        <div className="mpd-swap-llm-row">
          <input
            placeholder="Optional: what do you have on hand?"
            value={userHint}
            onInput={(e) => setUserHint((e.currentTarget as HTMLInputElement).value)}
          />
          <button
            type="button"
            className="btn"
            onClick={askLlm}
            disabled={suggesting}
            title="Ask the assistant for substitution ideas"
          >
            <Icon name="sparkle" size={13} />
            <span>{suggesting ? "Thinking…" : "Suggest"}</span>
          </button>
        </div>
        {suggestions.length > 0 && (
          <div className="mpd-swap-suggestions">
            {suggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                className="mpd-suggestion"
                onClick={() => pick(s)}
              >
                <div className="mpd-suggestion-text">
                  {s.text}
                  {s.quantity && <span className="mpd-suggestion-qty"> · {s.quantity}</span>}
                </div>
                {s.note && <div className="mpd-suggestion-note">{s.note}</div>}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AdditionForm({
  onCommit, onCancel,
}: {
  onCommit: (text: string, quantity?: string) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState("");
  const [qty, setQty] = useState("");
  return (
    <div className="mpd-add-form">
      <input
        placeholder="Ingredient (e.g. 1 tsp smoked paprika)"
        autoFocus
        value={text}
        onInput={(e) => setText((e.currentTarget as HTMLInputElement).value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && text.trim()) onCommit(text.trim(), qty || undefined);
          else if (e.key === "Escape") onCancel();
        }}
      />
      <input
        placeholder="qty"
        value={qty}
        onInput={(e) => setQty((e.currentTarget as HTMLInputElement).value)}
        className="mpd-add-qty"
      />
      <button type="button" className="btn" onClick={onCancel}>Cancel</button>
      <button
        type="button"
        className="btn btn-primary"
        disabled={!text.trim()}
        onClick={() => onCommit(text.trim(), qty || undefined)}
      >
        Add
      </button>
    </div>
  );
}

/** Surfaces removed rows with a one-tap restore. Quietly hidden when
 *  nothing's removed — querying the entry's overlay is cheap so we
 *  just re-derive from the parent's items + entry data. */
function RemovedRows({
  date, slot, onUnremove,
}: {
  date: string;
  slot: MealSlot;
  onUnremove: (recipeItemId: number) => void;
}) {
  const [removed, setRemoved] = useState<{ recipeItemId: number; originalText: string }[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.mealplanGetEffectiveItems({ date, slot });
        const list = r.entry.substitutions?.removals || [];
        if (!cancelled) setRemoved(list.map((x) => ({ recipeItemId: x.recipeItemId, originalText: x.originalText })));
      } catch { /* leave list empty */ }
    })();
    return () => { cancelled = true; };
  }, [date, slot]);
  if (removed.length === 0) return null;
  return (
    <div className="mpd-removed">
      <div className="mpd-removed-label">Removed for this day</div>
      {removed.map((r) => (
        <div key={r.recipeItemId} className="mpd-removed-row">
          <span className="mpd-removed-text">{r.originalText}</span>
          <button
            type="button"
            className="mpd-row-btn"
            onClick={() => onUnremove(r.recipeItemId)}
            title="Restore for this day"
          >
            <Icon name="plus" size={11} />
          </button>
        </div>
      ))}
    </div>
  );
}
